import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Mail, ArrowLeft, Calendar } from "lucide-react";

export default function PoliticaPrivacidade() {
  return (
    <div className="min-h-screen bg-hero-radial bg-white pb-12">
      <div className="max-w-3xl mx-auto px-5 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="flex items-center gap-2">
            <Logo size={32} />
          </Link>
          <Button asChild variant="ghost" size="sm">
            <Link to="/"><ArrowLeft className="w-4 h-4 mr-1" /> Voltar</Link>
          </Button>
        </div>

        <Card className="rounded-2xl">
          <CardContent className="p-6 sm:p-8 prose prose-sm max-w-none">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--accent))]/15 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-[hsl(var(--accent))]" />
              </div>
              <div>
                <h1 className="font-display text-2xl font-semibold text-[hsl(var(--primary))] m-0">Política de Privacidade</h1>
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <Calendar className="w-3 h-3" /> Última atualização: 14/05/2026
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed">
              Esta Política de Privacidade descreve como o <strong>CEAP BRASIL</strong> ("nós") coleta, usa e protege as informações pessoais dos alunos que utilizam nossa plataforma (aplicativo móvel e web).
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">1. Responsável pelo Tratamento</h2>
            <p className="text-sm text-muted-foreground">
              <strong>CEAP — Centro de Estudos da Administração Pública</strong> é o controlador dos dados pessoais coletados nesta plataforma, em conformidade com a Lei Geral de Proteção de Dados Pessoais (LGPD — Lei nº 13.709/2018).
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">2. Dados Coletados</h2>
            <p className="text-sm text-muted-foreground">Coletamos os seguintes dados pessoais durante o cadastro e uso da plataforma:</p>
            <ul className="text-sm text-muted-foreground list-disc ml-5 mt-2 space-y-1">
              <li><strong>Identificação:</strong> nome completo, CPF, e-mail, telefone</li>
              <li><strong>Vínculo profissional:</strong> cargo, órgão público, cidade, estado</li>
              <li><strong>Acadêmico:</strong> presenças em cursos, certificados emitidos, progresso, horas acumuladas, conquistas/nível</li>
              <li><strong>Técnico:</strong> endereço IP, tipo de dispositivo, navegador, logs de acesso</li>
              <li><strong>Opcional:</strong> @ do Instagram (informado em pesquisa de satisfação)</li>
            </ul>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">3. Finalidades do Tratamento</h2>
            <p className="text-sm text-muted-foreground">Os dados são utilizados para:</p>
            <ul className="text-sm text-muted-foreground list-disc ml-5 mt-2 space-y-1">
              <li>Identificação do aluno e controle de presença nos cursos (via QR Code)</li>
              <li>Emissão e validação de certificados digitais</li>
              <li>Acompanhamento do progresso acadêmico e gamificação</li>
              <li>Comunicação sobre cursos, calendário, materiais e novidades</li>
              <li>Pesquisas de satisfação para melhoria contínua dos serviços</li>
              <li>Atendimento de solicitações e suporte</li>
              <li>Cumprimento de obrigações legais e regulatórias</li>
            </ul>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">4. Base Legal</h2>
            <p className="text-sm text-muted-foreground">
              O tratamento dos seus dados é realizado com base no <strong>consentimento</strong> fornecido no momento do cadastro e na <strong>execução do contrato</strong> de prestação de serviços educacionais. Para dados estritamente necessários ao serviço (como CPF para emissão de certificados), o tratamento é amparado pelo <strong>legítimo interesse</strong>.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">5. Compartilhamento de Dados</h2>
            <p className="text-sm text-muted-foreground">Seus dados <strong>NÃO são vendidos</strong> a terceiros. Compartilhamos apenas com:</p>
            <ul className="text-sm text-muted-foreground list-disc ml-5 mt-2 space-y-1">
              <li><strong>Provedores de infraestrutura</strong> (hospedagem em nuvem) sob contrato de confidencialidade</li>
              <li><strong>Provedor de e-mail transacional</strong> (Resend) — apenas e-mail e nome, para envio de mensagens automáticas (recuperação de senha, notificações)</li>
              <li><strong>Autoridades públicas</strong> quando legalmente obrigados (ordem judicial, requisição oficial)</li>
            </ul>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">6. Armazenamento e Segurança</h2>
            <p className="text-sm text-muted-foreground">
              Adotamos medidas técnicas e administrativas para proteger seus dados, incluindo: criptografia de senhas com bcrypt, comunicação via HTTPS/TLS, controle de acesso por perfis, registros de auditoria e backups regulares. Os dados são armazenados em servidores no Brasil ou em jurisdições com nível adequado de proteção.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">7. Tempo de Retenção</h2>
            <p className="text-sm text-muted-foreground">
              Mantemos seus dados enquanto sua conta estiver ativa. Após inatividade prolongada (5 anos) ou solicitação de exclusão, os dados serão anonimizados ou excluídos, exceto aqueles que precisem ser mantidos por obrigação legal (ex.: histórico de certificados emitidos para validade pública).
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">8. Seus Direitos (LGPD)</h2>
            <p className="text-sm text-muted-foreground">Você tem direito a:</p>
            <ul className="text-sm text-muted-foreground list-disc ml-5 mt-2 space-y-1">
              <li>Confirmação da existência de tratamento e acesso aos dados</li>
              <li>Correção de dados incompletos, inexatos ou desatualizados</li>
              <li>Anonimização, bloqueio ou eliminação de dados desnecessários</li>
              <li>Portabilidade dos dados</li>
              <li>Eliminação dos dados pessoais tratados com consentimento</li>
              <li>Informação sobre compartilhamento</li>
              <li>Revogação do consentimento</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              Para exercer qualquer direito, entre em contato pelo e-mail abaixo.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">9. Cookies e Tecnologias de Rastreamento</h2>
            <p className="text-sm text-muted-foreground">
              Utilizamos apenas cookies <strong>essenciais</strong> para o funcionamento da plataforma (autenticação JWT, preferências). Não utilizamos cookies de terceiros para publicidade.
            </p>

            <h2 className="font-display text-base font-semibold text-[hsl(var(--primary))] mt-6 mb-2">10. Alterações nesta Política</h2>
            <p className="text-sm text-muted-foreground">
              Esta política pode ser atualizada para refletir mudanças legais, regulatórias ou operacionais. Notificaremos sobre alterações relevantes pelo e-mail cadastrado ou aviso no aplicativo.
            </p>

            <div className="mt-8 p-4 rounded-lg bg-[hsl(var(--muted))]/30 border border-[hsl(var(--border))]">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[hsl(var(--accent))] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-display font-semibold text-sm text-[hsl(var(--primary))]">Contato — Encarregado de Dados (DPO)</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    E-mail: <a href="mailto:administrativo@ceapbrasil.com" className="text-[hsl(var(--accent))] hover:underline">administrativo@ceapbrasil.com</a>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Responderemos sua solicitação em até <strong>15 dias úteis</strong>.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground text-center mt-8">
              CEAP BRASIL — Centro de Estudos da Administração Pública<br />
              Política em conformidade com a Lei Geral de Proteção de Dados (Lei 13.709/2018)
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
